import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { FileText, Calendar, RefreshCw, Receipt, Plus } from "lucide-react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Spinner } from "@/shared/components/ui/spinner";
import { cn } from "@/shared/lib/utils";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { parseSefazMessage } from "@/shared/lib/sefaz-message";
import { invoiceService } from "@/features/invoices/services/invoice.service";
import { useInvoiceList } from "@/features/invoices/hooks/useInvoiceList";
import type { InvoiceListFilters } from "@/features/invoices/hooks/useInvoiceList";
import type {
  InvoiceCategory,
  InvoiceResponseDTO,
  ManualInvoiceRequestDTO,
  ResendInvoiceRequestDTO,
} from "@/features/invoices/types/invoice.types";
import { InvoicesTable } from "@/features/invoices/components/InvoicesTable";
import { InvoiceFilters } from "@/features/invoices/components/InvoiceFilters";
import { InvoiceEditDialog } from "@/features/invoices/components/InvoiceEditDialog";
import type { InvoiceEditFormData } from "@/features/invoices/components/InvoiceEditDialog";
import { InvoiceDeleteDialog } from "@/features/invoices/components/InvoiceDeleteDialog";
import { InvoiceCancelDialog } from "@/features/invoices/components/InvoiceCancelDialog";
import { InvoiceResendDialog } from "@/features/invoices/components/InvoiceResendDialog";
import { ManualInvoiceDialog } from "@/features/invoices/components/ManualInvoiceDialog";
import { useInvoiceStatusPolling } from "@/features/invoices/hooks/useInvoiceStatusPolling";
import { Can } from "@/features/auth/components/Can";
import { ACTIONS } from "@/constants/roles";

const TAB = {
  RENTALS: "rentals",
  GRADUATIONS: "graduations",
  MANUALS: "manuals",
} as const;

type Tab = (typeof TAB)[keyof typeof TAB];

const DIALOG_MODE = {
  EDIT: "edit",
  DELETE: "delete",
  CANCEL: "cancel",
  RESEND: "resend",
} as const;

type DialogMode = (typeof DIALOG_MODE)[keyof typeof DIALOG_MODE];

const TABS: Array<{ id: Tab; label: string; icon: typeof FileText }> = [
  { id: TAB.RENTALS, label: "Locações", icon: FileText },
  { id: TAB.GRADUATIONS, label: "Formaturas", icon: Calendar },
  { id: TAB.MANUALS, label: "Manuais", icon: Receipt },
];

export function NotasFiscaisPage() {
  const [searchParams] = useSearchParams();
  // Deep-link `/notas-fiscais?rentalId=X` (vindo da modal de detalhes do
  // aluguel): pré-aplica o filtro na aba Locações. Lido uma única vez na
  // montagem — depois o estado dos filtros é soberano.
  const [deepLinkRentalId] = useState<number | undefined>(() => {
    const raw = searchParams.get("rentalId");
    const parsed = raw ? Number(raw) : NaN;
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  });

  const [activeTab, setActiveTab] = useState<Tab>(TAB.RENTALS);

  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [dialogContent, setDialogContent] = useState<DialogMode | null>(null);
  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoiceResponseDTO | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [emitOpen, setEmitOpen] = useState(false);
  const [emitLoading, setEmitLoading] = useState(false);

  const rentalsList = useInvoiceList({
    category: "RENTAL",
    enabled: activeTab === TAB.RENTALS,
    initialFilters: deepLinkRentalId
      ? { rentalId: deepLinkRentalId }
      : undefined,
  });
  const graduationsList = useInvoiceList({
    category: "GRADUATION",
    enabled: activeTab === TAB.GRADUATIONS,
  });
  const manualsList = useInvoiceList({
    category: "MANUAL",
    enabled: activeTab === TAB.MANUALS,
  });

  const activeList =
    activeTab === TAB.RENTALS
      ? rentalsList
      : activeTab === TAB.GRADUATIONS
        ? graduationsList
        : manualsList;

  const pollInvoiceStatus = useInvoiceStatusPolling(activeList.updateLocal);
  const activeCategory: InvoiceCategory =
    activeTab === TAB.RENTALS
      ? "RENTAL"
      : activeTab === TAB.GRADUATIONS
        ? "GRADUATION"
        : "MANUAL";

  const openDialog = useCallback(
    (mode: DialogMode, invoice: InvoiceResponseDTO) => {
      setSelectedInvoice(invoice);
      setDialogMode(mode);
      setDialogContent(mode);
    },
    [],
  );

  const closeDialog = useCallback(() => {
    setDialogMode(null);
  }, []);

  const handleApplyFilters = useCallback(
    (filters: InvoiceListFilters) => {
      activeList.setFilters(filters);
    },
    [activeList],
  );

  const handleClearFilters = useCallback(() => {
    activeList.setFilters({});
  }, [activeList]);

  const handleEditSubmit = async (data: InvoiceEditFormData) => {
    if (!selectedInvoice) return;
    setDialogLoading(true);
    try {
      const updated = await invoiceService.update(selectedInvoice.id, {
        rentalId: selectedInvoice.rentalId,
        externalId: data.externalId?.trim() ? data.externalId.trim() : null,
        value: data.value,
        status: data.status,
        type: data.type,
        paymentMethod: data.paymentMethod,
        issuedAt: new Date(data.issuedAt).toISOString(),
      });
      activeList.updateLocal(updated);
      toast.success("Nota fiscal atualizada");
      closeDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao atualizar nota fiscal"));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!selectedInvoice) return;
    setDialogLoading(true);
    try {
      await invoiceService.remove(selectedInvoice.id);
      activeList.removeLocal(selectedInvoice.id);
      toast.success("Nota fiscal excluída");
      closeDialog();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao excluir nota fiscal"));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleResendConfirm = async (data?: ResendInvoiceRequestDTO) => {
    if (!selectedInvoice) return;
    setDialogLoading(true);
    try {
      const resent = await invoiceService.resend(selectedInvoice.id, data);
      activeList.updateLocal(resent);
      if (resent.status === "ERROR") {
        setSelectedInvoice(resent);
        toast.error(
          parseSefazMessage(resent.errorDescription)?.message ??
            "A nota foi recusada de novo. Confira os dados antes de retentar.",
        );
      } else {
        toast.success(
          "Nota reenviada. Aguardando autorização — o status será atualizado em instantes.",
        );
        closeDialog();
        pollInvoiceStatus(resent.id);
      }
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao reenviar a nota fiscal"));
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!selectedInvoice) return;
    setDialogLoading(true);
    try {
      await invoiceService.cancel(selectedInvoice.id);
      toast.success("Cancelamento solicitado");
      activeList.reload();
      closeDialog();
    } catch (error: unknown) {
      toast.error(
        getApiErrorMessage(
          error,
          "Erro ao solicitar cancelamento da nota fiscal",
        ),
      );
    } finally {
      setDialogLoading(false);
    }
  };

  const handleEmit = async (data: ManualInvoiceRequestDTO) => {
    setEmitLoading(true);
    try {
      const invoice = await invoiceService.emit(data);
      if (invoice.status === "ERROR") {
        toast.error(
          parseSefazMessage(invoice.errorDescription)?.message ??
            "Falha na emissão da nota fiscal. Verifique os dados e tente novamente.",
        );
      } else {
        toast.success(
          "NF enviada. Aguardando autorização — o status será atualizado em instantes.",
        );
        setEmitOpen(false);
        pollInvoiceStatus(invoice.id);
      }
      manualsList.reload();
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Erro ao emitir nota fiscal"));
    } finally {
      setEmitLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Notas Fiscais</h1>
          <p className="text-muted-foreground">
            Gerencie notas fiscais emitidas pelo sistema.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 self-start md:self-auto">
          {activeTab === TAB.MANUALS && (
            <Can permission={ACTIONS.invoices.create}>
              <Button
                size="sm"
                onClick={() => setEmitOpen(true)}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Emitir NF
              </Button>
            </Can>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={activeList.reload}
            disabled={activeList.loading}
            className="gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </Button>
        </div>
      </div>

      <Card>
        <CardContent>
          <div className="flex gap-1 border-b mb-6 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap",
                    isActive
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground",
                  )}
                  aria-pressed={isActive}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="space-y-6">
            <InvoiceFilters
              category={activeCategory}
              filters={activeList.filters}
              loading={activeList.loading}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
            />

            {activeList.loading && activeList.data.length === 0 ? (
              <div className="flex justify-center items-center py-16">
                <Spinner className="w-6 h-6" />
              </div>
            ) : activeList.error && activeList.data.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-16 text-center">
                <p className="text-destructive">{activeList.error}</p>
                <Button variant="outline" size="sm" onClick={activeList.reload}>
                  Tentar novamente
                </Button>
              </div>
            ) : activeList.data.length === 0 ? (
              <div className="py-16 text-center text-muted-foreground">
                Nenhuma nota fiscal encontrada para os filtros aplicados.
              </div>
            ) : (
              <InvoicesTable
                invoices={activeList.data}
                category={activeCategory}
                loading={activeList.loading}
                currentPage={activeList.page}
                totalPages={activeList.totalPages}
                onEdit={(invoice) => openDialog(DIALOG_MODE.EDIT, invoice)}
                onCancel={(invoice) => openDialog(DIALOG_MODE.CANCEL, invoice)}
                onDelete={(invoice) => openDialog(DIALOG_MODE.DELETE, invoice)}
                onResend={(invoice) => openDialog(DIALOG_MODE.RESEND, invoice)}
                onPageChange={activeList.setPage}
              />
            )}
          </div>
        </CardContent>
      </Card>

      {dialogContent === DIALOG_MODE.EDIT && (
        <InvoiceEditDialog
          open={dialogMode === DIALOG_MODE.EDIT}
          loading={dialogLoading}
          invoice={selectedInvoice}
          onClose={closeDialog}
          onSubmit={handleEditSubmit}
        />
      )}
      {dialogContent === DIALOG_MODE.DELETE && (
        <InvoiceDeleteDialog
          open={dialogMode === DIALOG_MODE.DELETE}
          loading={dialogLoading}
          invoice={selectedInvoice}
          onClose={closeDialog}
          onConfirm={handleDeleteConfirm}
        />
      )}
      {dialogContent === DIALOG_MODE.CANCEL && (
        <InvoiceCancelDialog
          open={dialogMode === DIALOG_MODE.CANCEL}
          loading={dialogLoading}
          invoice={selectedInvoice}
          onClose={closeDialog}
          onConfirm={handleCancelConfirm}
        />
      )}

      {dialogContent === DIALOG_MODE.RESEND && (
        <InvoiceResendDialog
          key={selectedInvoice?.id}
          open={dialogMode === DIALOG_MODE.RESEND}
          loading={dialogLoading}
          invoice={selectedInvoice}
          category={activeCategory}
          onClose={closeDialog}
          onConfirm={handleResendConfirm}
        />
      )}

      {emitOpen && (
        <ManualInvoiceDialog
          open
          loading={emitLoading}
          onClose={() => setEmitOpen(false)}
          onSubmit={handleEmit}
        />
      )}
    </div>
  );
}
